import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { hydrateCache } from '@common/core-layer/utils/cache-hydration.utils'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { useNuxtApp } from 'nuxt/app'
import type { LibraryProducts } from '@courses/types-layer/types/library'
import { isCourseCheckoutProductId, shouldSkipCoursesAccessGuardForGokollabRoute } from '../utils/courses-route-segment.utils'
import {
  coursesAccessGuardQueryKeys,
  fetchCourseAccessData,
  type CourseAccessData,
  fetchBasicProductInfo,
  type BasicProductInfo,
} from '../services/courses-access-guard.service'
import type { QueryClient } from '@tanstack/vue-query'
import type { Pinia } from 'pinia'

const libraryQueryKeys = {
  list: (locId: string) => ['library', locId, 'list'] as const,
}

const PUBLIC_OFFERS_ROUTE_PREFIX = '/courses/offers/'

/**
 * Courses Access Control Middleware
 *
 * Authentication and access check for course routes.
 * Redirects unauthenticated users to home page.
 * Redirects users without access to checkout page.
 *
 * Flow:
 * 1. Check if route is a courses route (default or communities context)
 * 2. Skip if already on checkout page (prevent redirect loop)
 * 3. Check if user is authenticated
 * 4. Check library cache first — if product is in the user's library, grant access
 * 5. If library cache is absent, defer guard (allow route without access check)
 * 6. If library cache exists but product not found, fall back to access API
 */
export default defineNuxtRouteMiddleware(async to => {
  const isCoursesRoute = to.path.startsWith('/courses') || to.path.includes('/learning/courses/')
  if (!isCoursesRoute) {
    return
  }

  if (to.path.startsWith(PUBLIC_OFFERS_ROUTE_PREFIX)) {
    return
  }

  if (to.name === 'courses-checkout' || to.name === 'courses-purchase-product') {
    return
  }

  if (shouldSkipCoursesAccessGuardForGokollabRoute(to.path, to.name)) {
    return
  }

  const coursesSegmentMatch = to.path.match(/^\/courses\/([^/]+)$/)
  const coursesSegment = coursesSegmentMatch?.[1]
  if (coursesSegment && isCourseCheckoutProductId(coursesSegment)) {
    return
  }

  const { CPNavigateTo } = useCPNavigation()
  const redirectToHome = () => {
    return CPNavigateTo({
      name: 'index',
      options: { replace: true, redirectCode: 302 },
    })
  }

  const redirectToCheckoutPage = (productId: string) => {
    return CPNavigateTo(
      {
        name: 'courses-checkout',
        params: { productId },
        options: { replace: true, redirectCode: 302 },
      },
      true
    )
  }

  const nuxtApp = useNuxtApp()
  const env = process.server ? 'SERVER' : 'CLIENT'

  try {
    const pinia = nuxtApp.$pinia as Pinia
    const authStore = useAuthStore(pinia)
    const isAuthenticated = authStore.isLoggedIn
    const hasCoursesRouteMatch = to.matched.some(record => record.path.startsWith('/courses') || record.path.includes('/learning/courses/'))
    const isUnavailableCoursesRoute = !hasCoursesRouteMatch

    if (!isAuthenticated || isUnavailableCoursesRoute) {
      console.log(`❌ [Courses Access] ${env}: Blocked route (${to.path}), redirecting to home`)
      return redirectToHome()
    }

    const productId = to.params.productId as string | undefined
    if (!productId) {
      return
    }

    const queryClient = nuxtApp.$queryClient as QueryClient | undefined
    if (!queryClient) {
      return
    }

    const { locationId } = useLocation()
    if (!locationId.value) {
      console.log(`❌ [Courses Access] ${env}: No locationId, redirecting to home`)
      return redirectToHome()
    }

    // Library-first access check: uses the same query key as useLibraryQueryStore
    const cachedLibraryProducts = queryClient.getQueryData<LibraryProducts>(libraryQueryKeys.list(locationId.value))

    if (!cachedLibraryProducts) {
      console.log(`⏳ [Courses Access] ${env}: Library cache not ready, deferring guard for ${to.path}`)
    }

    if (cachedLibraryProducts && cachedLibraryProducts.some(product => product.id === productId)) {
      console.log(`✅ [Courses Access] ${env}: Access granted via library cache for ${to.path}`)
      return
    }

    // Product not in library cache — fall back to access API
    const source = 'communities'

    const basicProductInfo = (await hydrateCache({
      queryClient,
      queryKey: coursesAccessGuardQueryKeys.basicProductInfo(productId, locationId.value),
      fetchFn: () => fetchBasicProductInfo({ courseId: productId, locationId: locationId.value }),
    })) as BasicProductInfo | null

    if (!basicProductInfo || !basicProductInfo.locationId) {
      console.log(`❌ [Courses Access] ${env}: Basic product info not found, redirecting to home`)
      return redirectToHome()
    }

    const accessData = (await hydrateCache({
      queryClient,
      queryKey: coursesAccessGuardQueryKeys.productAccess(productId, basicProductInfo.locationId, source),
      fetchFn: () =>
        fetchCourseAccessData({
          courseId: productId,
          locationId: basicProductInfo.locationId,
          source,
        }),
    })) as CourseAccessData | null

    if (accessData?.hasAccess === false) {
      console.log(`❌ [Courses Access] ${env}: Access denied for ${to.path}, redirecting to checkout`)
      return redirectToCheckoutPage(productId)
    }

    console.log(`✅ [Courses Access] ${env}: Access granted for ${to.path}`)
  } catch (error) {
    console.error(`❌ [Courses Access] ${env}: Access control failed:`, error)
    return redirectToHome()
  }
})
